import { format } from 'date-fns';
import { Check, MessageCircleQuestion } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Skeleton } from 'src/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Survey, SurveyStatus } from 'src/redux/survey/survey.types';
import DashboardSurveyPagination from '../components/dashboard-survey-pagination.component';
import DashboardSurveyToolbar from '../components/dashboard-survey-toolbar';
import { useDashboardSurveyList } from '../components/useDashboardSurveyList';

function getStatusColor(status: SurveyStatus, isDot: boolean = false) {
  switch (status) {
    case SurveyStatus.Draft:
      return isDot ? 'bg-gray-600' : 'text-gray-500';
    case SurveyStatus.Live:
      return isDot ? 'bg-blue-500' : 'text-blue-500';
    case SurveyStatus.Complete:
      return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
    default:
      return isDot ? 'bg-gray-600' : 'text-gray-600';
  }
}

export function CombineSurveySelectStep(props: {
  selectedIds: string[];
  onToggleSurvey: (survey: Survey) => void;
  onNext: () => void;
}) {
  const { selectedIds, onToggleSurvey, onNext } = props;
  const { listRows, isFetching, isListEmpty } = useDashboardSurveyList();

  return (
    <div className="space-y-6 w-full">
      <p className="text-sm text-gray-600">
        Select at least two surveys. You will choose an anchor survey next; other surveys map onto it.
        Use the same search and filters as the main survey list (status, mine/all, search).
      </p>

      <DashboardSurveyToolbar />

      {isListEmpty && !isFetching ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-600">
          No surveys match your filters. Try adjusting search or status filters.
        </div>
      ) : (
        <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex justify-between bg-gray-201">
            <div className="flex items-center justify-between w-full">
              <div className="flex gap-2 p-2 pl-4 items-center font-medium">
                <MessageCircleQuestion className="w-4 h-4" />
                Select surveys to combine
              </div>
            </div>
          </div>
          <Table>
            <TableHeader className="p-0 text-sm border-b border-gray-201">
              <TableRow className="border-none">
                <TableHead
                  className="w-10 text-center h-8 text-gray-600"
                  aria-hidden
                />
                <TableHead className="w-[88px] max-w-[88px] h-8 text-gray-600">
                  Status
                </TableHead>
                <TableHead className="text-gray-600 h-8">
                  Title
                </TableHead>
                <TableHead className="text-gray-600 h-8">
                  Created
                </TableHead>
                <TableHead className="text-gray-600 text-center h-8">
                  Questions
                </TableHead>
                <TableHead className="text-gray-600 text-center h-8">
                  Starts
                </TableHead>
                <TableHead className="text-gray-600 text-center h-8">
                  Completes
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="rounded-none">
              {listRows.map((row, index) => {
                if (row.kind !== 'survey') {
                  return null;
                }
                const survey = row.survey;
                const stripe = index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]';
                const selected = survey ? selectedIds.includes(survey.extSurveyId) : false;
                const rowClass = selected
                  ? 'bg-blue-50 ring-2 ring-inset ring-blue-400 hover:bg-blue-50/90'
                  : `${stripe} hover:bg-gray-100`;

                return (
                  <TableRow
                    key={survey ? `pick-${survey.extSurveyId}` : `skel-${index}`}
                    role="button"
                    tabIndex={survey ? 0 : -1}
                    aria-pressed={selected}
                    aria-label={survey ? `Select survey ${survey.title}` : undefined}
                    onClick={() => survey && onToggleSurvey(survey)}
                    onKeyDown={(e) => {
                      if (!survey) {
                        return;
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleSurvey(survey);
                      }
                    }}
                    className={`transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer ${rowClass}`}
                  >
                    <TableCell className="w-10 text-center py-1.5 align-middle">
                      {survey ? (
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                            selected
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-gray-300 bg-white'
                          }`}
                          aria-hidden
                        >
                          {selected ? (
                            <Check
                              className="h-3.5 w-3.5"
                              strokeWidth={3}
                            />
                          ) : null}
                        </span>
                      ) : (
                        <Skeleton className="h-6 w-6 rounded-full mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {survey ? (
                        <span
                          className={`max-w-[88px] inline-flex items-center justify-center py-1 px-2 text-xs rounded-full font-bold bg-white shadow-sm ${getStatusColor(survey.surveyStatus)}`}
                        >
                          {survey.surveyStatus !== SurveyStatus.Draft && (
                            <div className={`size-1 rounded-full mr-1 ${getStatusColor(survey.surveyStatus, true)}`} />
                          )}
                          {survey.surveyStatus.charAt(0).toUpperCase() + survey.surveyStatus.slice(1)}
                        </span>
                      ) : (
                        <Skeleton className="w-16 h-[26px] mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium py-1.5">
                      {survey ? survey.title : <Skeleton className="w-48 h-4" />}
                    </TableCell>
                    <TableCell className="py-1.5">
                      {survey ? format(new Date(survey.createdAt), 'MMM d, yyyy') : <Skeleton className="w-24 h-4" />}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {survey ? survey.questionCount : <Skeleton className="w-8 h-4 mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {survey ? (
                        survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete ? (
                          <span className="text-sm">
                            {survey.started.human + survey.started.ai}
                          </span>
                        ) : (
                          <span className="text-sm">
                            -
                          </span>
                        )
                      ) : (
                        <Skeleton className="w-8 h-4 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center py-1.5">
                      {survey ? (
                        survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete ? (
                          <span className="text-sm">
                            {survey.completed.human + survey.completed.ai}
                          </span>
                        ) : (
                          <span className="text-sm">
                            -
                          </span>
                        )
                      ) : (
                        <Skeleton className="w-8 h-4 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DashboardSurveyPagination />

      <div className="flex flex-wrap items-center gap-4">
        <p className="text-sm text-gray-600">
          {selectedIds.length}
          {' '}
          survey
          {selectedIds.length === 1 ? '' : 's'}
          {' '}
          selected
        </p>
        <Button
          type="button"
          disabled={selectedIds.length < 2}
          onClick={onNext}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
